import { queryGet } from '@apeiron/common'
import { StarLite } from '@apeiron/star'
import { Box, Container as MuiContainer, styled } from '@mui/material'
import OrbitalTrackAttachDialog from '@src/components/orbitalTrack/attach/OrbitalTrackAttachDialog'
import OrbitalTrackStarInfo from '@src/components/orbitalTrack/detail/OrbitalTrackStarInfo'
import PlanetOnTrackSection from '@src/components/orbitalTrack/detail/PlanetOnTrackSection'
import RentalSection from '@src/components/orbitalTrack/rentalSetting/RentalSection'
import StarDetailCard from '@src/components/stars/cards/StarDetailCard'
import StarDetailCardHoverEffect from '@src/components/stars/cards/StarDetailCardHoverEffect'
import StarSelectPopup from '@src/components/stars/list/StarSelectPopup'
import { ROUTE } from '@src/constants/route'
import { useOrbitalTrackAttachContext } from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import { OrbitalTrackRentalSettingProvider } from '@src/contexts/orbitalTrack/OrbitalTrackRentalSettingContext'
import { useOnlineStarHelper } from '@src/hooks/apeiron/star/useStarHelper'
import { useRouter } from 'next/router'
import queryString from 'query-string'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, useCallback, useEffect } from 'react'

const Container = styled(MuiContainer)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding-bottom: 20px;
`

const TopContainer = styled(Box)`
  display: flex;
  gap: 30px;
  justify-content: center;
  margin-top: 50px;
  ${props => props.theme.breakpoints.up('res1280')} {
    flex-direction: row;
    width: 100%;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    align-items: center;
    flex-direction: column;
    width: 95%;
  }
`

const TopRightContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${props => props.theme.breakpoints.up('res1280')} {
    flex: 1;
    margin-top: 10px;
    max-width: 832px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    width: 100%;
  }
`

const BottomContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 95%;
`

const OrbitalTrackDetail: FC<Props> = props => {
  const { className } = props

  const router = useRouter()

  const starId = queryGet<string>(router.query, 'starId') || ''

  const { isOwner, loading: checkingOwner } = useOnlineStarHelper(starId)

  const {
    star,
    showAttachPlanet,
    showStarSelect,
    trackDetail,
    refetchStar,
    resetSelect,
    setShowAttachPlanet,
    setShowStarSelect,
    setStar,
    setStarId
  } = useOrbitalTrackAttachContext()

  const handleOnCardClick = useCallback(() => {
    setShowStarSelect(true)
  }, [setShowStarSelect])

  const handleOnCloseAttachPlanet = useCallback(
    (success: boolean) => {
      if (success) {
        refetchStar()
      }

      setShowAttachPlanet(false)
    },
    [refetchStar, setShowAttachPlanet]
  )

  const handleOnAttachPlanet = useCallback(() => {
    resetSelect()
    setShowAttachPlanet(true)
  }, [resetSelect, setShowAttachPlanet])

  const handleOnStars = useCallback(
    (stars: StarLite[]) => {
      return R.map((fetchedStar: StarLite): StarLite => {
        const disabled = fetchedStar.id === star?.id

        return R.assoc('disabled', disabled, fetchedStar) as StarLite
      }, stars)
    },
    [star]
  )

  const handleOnSelectStarClose = useCallback(() => {
    setShowStarSelect(false)
  }, [setShowStarSelect])

  const handleOnSelectStar = useCallback(
    (star: StarLite) => {
      setStar(null)

      const url = queryString.stringifyUrl({
        url: ROUTE.GODIVERSE_MANAGE_ORBITAL_TRACK,
        query: { starId: star.id }
      })

      router.push(url, undefined, { shallow: true })
      handleOnSelectStarClose()
    },
    [handleOnSelectStarClose, router, setStar]
  )

  useEffect(() => {
    if (!checkingOwner && RA.isNotNilOrEmpty(starId)) {
      if (isOwner) {
        setStarId(starId)
      } else {
        router.replace(ROUTE.GODIVERSE_MANAGE_ORBITAL_TRACK)
      }
    }
  }, [checkingOwner, isOwner, starId])

  return (
    <Container maxWidth={'res1280'} className={className}>
      <TopContainer>
        <StarDetailCard
          isBookmarkable={false}
          isBookmarked={false}
          showNameHeader={false}
          showSelectMode={R.isNil(star)}
          hoverComponent={<StarDetailCardHoverEffect />}
          star={star}
          onClick={handleOnCardClick}
        />
        <TopRightContainer>
          <OrbitalTrackStarInfo />
          <PlanetOnTrackSection onAttach={handleOnAttachPlanet} />
        </TopRightContainer>
      </TopContainer>
      {!R.isNil(trackDetail) && (
        <BottomContainer>
          <OrbitalTrackRentalSettingProvider>
            <RentalSection />
          </OrbitalTrackRentalSettingProvider>
        </BottomContainer>
      )}
      <StarSelectPopup
        open={showStarSelect}
        onClose={handleOnSelectStarClose}
        onSelect={handleOnSelectStar}
        postProcessData={handleOnStars}
      />
      <OrbitalTrackAttachDialog
        isOwner
        isOpened={showAttachPlanet}
        onClose={handleOnCloseAttachPlanet}
      />
    </Container>
  )
}

type Props = {
  className?: string
}
export default OrbitalTrackDetail
